/**
 * `Beats: BeatApi` — exact rational beats (W1-01 core-time-math; contract: core/time/types.ts, DESIGN.md D2).
 *
 * A Beat IS a normalised RPE triple `w + n/d` (w i32, 0 <= n < d, gcd(n, d) = 1, n = 0 => d = 1, d <= 2^32 - 1),
 * so export is the identity and Phira's `a + b / c` f64 value (rpe-format.md F§3.1) is reproduced bit for bit.
 *
 * Arithmetic runs on doubles while every product and sum stays a safe integer (the common case: snap-grid
 * denominators) and falls back to BigInt otherwise; comparisons never round. A COMPUTED result whose reduced
 * denominator exceeds 2^32 - 1 is rounded to the nearest multiple of 1/80640 (ties toward +infinity) and counted
 * in the optional `RoundingReport`; constructors from data (`make`, `fromTriple`, `fromDecimalString`) never round.
 * Results that leave the i32 range of `w` throw a RangeError.
 *
 * Snap divisors: `snap` / `floorTo` / `ceilTo` / `step` accept any positive safe integer `div`. For div <= 2^32 - 1
 * the result is always an exact multiple of 1/div. For a larger div the exact multiple can need a denominator
 * above 2^32 - 1; it is then a computed result like any other and falls back to the nearest multiple of 1/80640
 * (so it is NOT a multiple of 1/div). `BeatApi` has no report parameter on these four; callers that must detect
 * that fallback use `quantizeBeat` / `stepBeat` below, which count it in a `RoundingReport`.
 */

import {
  type BigRational,
  bestRational,
  dyadicOf,
  floorDiv,
  floorDivBig,
  gcd,
  gcdBig,
  Rat,
  roundHalfUpDivBig,
} from '../math/rational';
import type { Beat, BeatApi, RoundingReport, Triple } from './types';

/** Smallest RPE beat integer part (i32). */
export const I32_MIN = -2147483648;
/** Largest RPE beat integer part (i32). */
export const I32_MAX = 2147483647;
/** Largest RPE denominator (u32). */
export const U32_MAX = 4294967295;
/** Grid that computed results fall back to when their denominator overflows u32 (DESIGN.md D2). */
export const OVERFLOW_DIV = 80640;

const I32_MIN_BIG = -2147483648n;
const I32_MAX_BIG = 2147483647n;
const U32_MAX_BIG = 4294967295n;
const OVERFLOW_DIV_BIG = 80640n;

const ZERO: Beat = Object.freeze({ w: 0, n: 0, d: 1 });
const ONE: Beat = Object.freeze({ w: 1, n: 0, d: 1 });

// ---------------------------------------------------------------------------------------------------------------
// Internal constructors
// ---------------------------------------------------------------------------------------------------------------

function outOfRange(w: number | bigint): never {
  throw new RangeError(`beat out of range: integer part ${String(w)} is outside i32`);
}

/** Trusted parts (0 <= n < d, gcd = 1, d <= U32): checks the i32 range and canonicalises -0 and integers. */
function fin(w: number, n: number, d: number): Beat {
  if (w < I32_MIN || w > I32_MAX) outOfRange(w);
  const ww = w === 0 ? 0 : w;
  return n === 0 ? { w: ww, n: 0, d: 1 } : { w: ww, n, d };
}

/** Normalises the BigInt improper fraction N/D; null when it is not representable without rounding. */
function normBigExact(num: bigint, den: bigint): Beat | null {
  let n = num;
  let d = den;
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  const g = gcdBig(n, d);
  if (g > 1n) {
    n /= g;
    d /= g;
  }
  if (d > U32_MAX_BIG) return null;
  const w = floorDivBig(n, d);
  if (w < I32_MIN_BIG || w > I32_MAX_BIG) return null;
  const r = n - w * d;
  return fin(Number(w), Number(r), Number(d));
}

/** Normalises the BigInt improper fraction N/D (D != 0) applying the overflow policy. */
function normBig(num: bigint, den: bigint, report: RoundingReport | undefined): Beat {
  let n = num;
  let d = den;
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  let g = gcdBig(n, d);
  if (g > 1n) {
    n /= g;
    d /= g;
  }
  if (d > U32_MAX_BIG) {
    n = roundHalfUpDivBig(n * OVERFLOW_DIV_BIG, d);
    d = OVERFLOW_DIV_BIG;
    g = gcdBig(n, d);
    if (g > 1n) {
      n /= g;
      d /= g;
    }
    if (report !== undefined) report.rounded += 1;
  }
  const w = floorDivBig(n, d);
  if (w < I32_MIN_BIG || w > I32_MAX_BIG) outOfRange(w);
  return fin(Number(w), Number(n - w * d), Number(d));
}

/** Normalises the improper fraction N/D of safe integers (D > 0), applying the overflow policy. */
function normNum(num: number, den: number, report: RoundingReport | undefined): Beat {
  if (num === 0) return ZERO;
  const g = gcd(num, den);
  const n0 = num / g;
  const d = den / g;
  if (d > U32_MAX) return normBig(BigInt(n0), BigInt(d), report);
  const w = floorDiv(n0, d);
  const wd = w * d;
  if (!Number.isSafeInteger(wd)) return normBig(BigInt(n0), BigInt(d), report);
  return fin(w, n0 - wd, d);
}

/**
 * w + k/div with 0 <= k <= div (div a positive safe integer): carries and reduces. A reduced denominator above
 * 2^32 - 1 (only possible for div > 2^32 - 1) takes the overflow policy.
 */
function fracBeat(w: number, k: number, div: number, report: RoundingReport | undefined): Beat {
  if (k === 0) return fin(w, 0, 1);
  if (k === div) return fin(w + 1, 0, 1);
  const g = gcd(k, div);
  const d = div / g;
  if (d > U32_MAX) return normBig(BigInt(w) * BigInt(d) + BigInt(k / g), BigInt(d), report);
  return fin(w, k / g, d);
}

function bigOf(b: Beat): { readonly num: bigint; readonly den: bigint } {
  const d = BigInt(b.d);
  return { num: BigInt(b.w) * d + BigInt(b.n), den: d };
}

function requireInt(x: number, what: string): void {
  if (!Number.isInteger(x)) throw new RangeError(`beat: ${what} must be an integer (got ${String(x)})`);
}

function requireDiv(div: number): void {
  if (!Number.isSafeInteger(div) || div <= 0) {
    throw new RangeError(`beat: snap divisor must be a positive integer (got ${String(div)})`);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------------------------------------------

function make(w: number, n = 0, d = 1): Beat {
  requireInt(w, 'w');
  requireInt(n, 'n');
  requireInt(d, 'd');
  if (d <= 0) throw new RangeError(`beat: denominator must be positive (got ${String(d)})`);
  if (n === 0) return fromInt(w);
  if (Number.isSafeInteger(w) && Number.isSafeInteger(n) && d <= U32_MAX) {
    if (n >= 0 && n < d) {
      const g = gcd(n, d);
      return fin(w, n / g, d / g);
    }
    const q = floorDiv(n, d);
    const qd = q * d;
    const ww = w + q;
    if (Number.isSafeInteger(qd) && Number.isSafeInteger(ww)) {
      const r = n - qd;
      const g = gcd(r, d);
      return fin(ww, r / g, d / g);
    }
  }
  const D = BigInt(d);
  const beat = normBigExact(BigInt(w) * D + BigInt(n), D);
  if (beat === null) throw new RangeError(`beat: ${String(w)} + ${String(n)}/${String(d)} is not representable`);
  return beat;
}

function fromInt(w: number): Beat {
  requireInt(w, 'w');
  if (w < I32_MIN || w > I32_MAX) outOfRange(w);
  return w === 0 ? ZERO : { w, n: 0, d: 1 };
}

function isValidTriple(t: Triple): boolean {
  const [a, b, c] = t;
  return (
    Number.isInteger(a) &&
    a >= I32_MIN &&
    a <= I32_MAX &&
    Number.isInteger(b) &&
    b >= 0 &&
    b <= U32_MAX &&
    Number.isInteger(c) &&
    c >= 1 &&
    c <= U32_MAX
  );
}

function tryFromTriple(t: Triple): { readonly beat: Beat; readonly normalized: boolean } | null {
  const raw: unknown = t;
  if (!Array.isArray(raw) || raw.length !== 3 || !isValidTriple(t)) return null;
  const [a, b, c] = t;
  if (b === 0) return { beat: fin(a === 0 ? 0 : a, 0, 1), normalized: c !== 1 };
  let w = a;
  let n = b;
  if (n >= c) {
    const q = floorDiv(n, c);
    w += q;
    n -= q * c;
  }
  if (w < I32_MIN || w > I32_MAX) return null;
  if (n === 0) return { beat: fin(w, 0, 1), normalized: true };
  const g = gcd(n, c);
  return { beat: fin(w, n / g, c / g), normalized: b >= c || g !== 1 };
}

function fromTriple(t: Triple): { readonly beat: Beat; readonly normalized: boolean } {
  const r = tryFromTriple(t);
  if (r === null) throw new RangeError(`beat: invalid RPE triple [${t.map(String).join(', ')}]`);
  return r;
}

function fromDecimalString(s: string): Beat | null {
  const r = Rat.fromDecimal(s);
  return r === null ? null : normBigExact(r.num, r.den);
}

function fromF64(x: number, maxDen: number): Beat {
  if (!Number.isFinite(x)) throw new RangeError(`beat: not a finite number (${String(x)})`);
  if (!Number.isInteger(maxDen) || maxDen < 1) throw new RangeError(`beat: maxDen must be a positive integer`);
  if (Number.isInteger(x)) return fromInt(x);
  const best = bestRational(dyadicOf(x), BigInt(Math.min(maxDen, U32_MAX)));
  return normBig(best.num, best.den, undefined);
}

// ---------------------------------------------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------------------------------------------

function cmp(a: Beat, b: Beat): -1 | 0 | 1 {
  if (a.w !== b.w) return a.w < b.w ? -1 : 1;
  if (a.d === b.d) return a.n < b.n ? -1 : a.n > b.n ? 1 : 0;
  const x = a.n * b.d;
  const y = b.n * a.d;
  if (x <= Number.MAX_SAFE_INTEGER && y <= Number.MAX_SAFE_INTEGER) return x < y ? -1 : x > y ? 1 : 0;
  const X = BigInt(a.n) * BigInt(b.d);
  const Y = BigInt(b.n) * BigInt(a.d);
  return X < Y ? -1 : X > Y ? 1 : 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Arithmetic
// ---------------------------------------------------------------------------------------------------------------

/** (w1 + n1/d1) + (w2 + n2/d2) with both fractions in [0, 1). */
function addParts(
  w1: number,
  n1: number,
  d1: number,
  w2: number,
  n2: number,
  d2: number,
  report: RoundingReport | undefined,
): Beat {
  let w = w1 + w2;
  if (n1 === 0) return n2 === 0 ? fin(w, 0, 1) : fin(w, n2, d2);
  if (n2 === 0) return fin(w, n1, d1);
  if (d1 === d2) {
    let n = n1 + n2;
    if (n >= d1) {
      n -= d1;
      w += 1;
    }
    if (n === 0) return fin(w, 0, 1);
    const g = gcd(n, d1);
    return fin(w, n / g, d1 / g);
  }
  const g = gcd(d1, d2);
  const a = d1 / g;
  const b = d2 / g;
  const d = a * d2;
  const x = n1 * b;
  const y = n2 * a;
  let n = x + y;
  if (Number.isSafeInteger(d) && Number.isSafeInteger(n)) {
    if (n >= d) {
      n -= d;
      w += 1;
    }
    if (n === 0) return fin(w, 0, 1);
    const g2 = gcd(n, d);
    const dd = d / g2;
    if (dd <= U32_MAX) return fin(w, n / g2, dd);
  }
  const D1 = BigInt(d1);
  const D2 = BigInt(d2);
  const D = D1 * D2;
  return normBig(BigInt(w1 + w2) * D + BigInt(n1) * D2 + BigInt(n2) * D1, D, report);
}

function add(a: Beat, b: Beat, report?: RoundingReport): Beat {
  return addParts(a.w, a.n, a.d, b.w, b.n, b.d, report);
}

function sub(a: Beat, b: Beat, report?: RoundingReport): Beat {
  return b.n === 0
    ? addParts(a.w, a.n, a.d, -b.w, 0, 1, report)
    : addParts(a.w, a.n, a.d, -b.w - 1, b.d - b.n, b.d, report);
}

function mulInt(a: Beat, k: number, report?: RoundingReport): Beat {
  requireInt(k, 'k');
  if (k === 0) return ZERO;
  const wk = a.w * k;
  const nk = a.n * k;
  if (Number.isSafeInteger(wk) && Number.isSafeInteger(nk)) {
    if (a.n === 0) return fin(wk, 0, 1);
    const q = floorDiv(nk, a.d);
    const qd = q * a.d;
    const w = wk + q;
    if (Number.isSafeInteger(qd) && Number.isSafeInteger(w)) {
      const r = nk - qd;
      if (r === 0) return fin(w, 0, 1);
      const g = gcd(r, a.d);
      return fin(w, r / g, a.d / g);
    }
  }
  const { num, den } = bigOf(a);
  return normBig(num * BigInt(k), den, report);
}

function mulRat(a: Beat, p: number, q: number, report?: RoundingReport): Beat {
  requireInt(p, 'p');
  requireInt(q, 'q');
  if (q <= 0) throw new RangeError(`beat: mulRat divisor must be positive (got ${String(q)})`);
  if (p === 0) return ZERO;
  const wd = a.w * a.d;
  const whole = wd + a.n;
  const num = whole * p;
  const den = a.d * q;
  if (
    Number.isSafeInteger(wd) &&
    Number.isSafeInteger(whole) &&
    Number.isSafeInteger(num) &&
    Number.isSafeInteger(den)
  ) {
    return normNum(num, den, report);
  }
  const big = bigOf(a);
  return normBig(big.num * BigInt(p), big.den * BigInt(q), report);
}

function divInt(a: Beat, k: number, report?: RoundingReport): Beat {
  requireInt(k, 'k');
  if (k === 0) throw new RangeError('beat: division by zero');
  return k < 0 ? mulRat(a, -1, -k, report) : mulRat(a, 1, k, report);
}

// ---------------------------------------------------------------------------------------------------------------
// Snapping
// ---------------------------------------------------------------------------------------------------------------

/** Rounding direction of `quantizeBeat`: nearest (ties toward +infinity), down, or up. */
export type QuantizeMode = 'round' | 'floor' | 'ceil';

/**
 * Multiple of 1/div nearest to b ('round', ties toward +infinity), at or below it ('floor') or at or above it
 * ('ceil'). Throws RangeError unless div is a positive safe integer. For div > 2^32 - 1 a result whose reduced
 * denominator overflows u32 falls back to the 1/80640 grid and is counted in `report` (module header).
 */
function quantize(b: Beat, div: number, mode: QuantizeMode, report?: RoundingReport): Beat {
  requireDiv(div);
  if (b.n === 0) return b;
  const nd = b.n * div;
  const twice = 2 * nd + b.d;
  if (Number.isSafeInteger(twice)) {
    const k = mode === 'round' ? floorDiv(twice, 2 * b.d) : mode === 'floor' ? floorDiv(nd, b.d) : -floorDiv(-nd, b.d);
    return fracBeat(b.w, k, div, report);
  }
  const N = BigInt(b.n) * BigInt(div);
  const D = BigInt(b.d);
  const K = mode === 'round' ? roundHalfUpDivBig(N, D) : mode === 'floor' ? floorDivBig(N, D) : -floorDivBig(-N, D);
  const DIV = BigInt(div);
  return normBig(BigInt(b.w) * DIV + K, DIV, report);
}

/**
 * b + k/div. Throws RangeError unless div is a positive safe integer and k an integer. A result whose reduced
 * denominator overflows u32 (only possible for div > 2^32 - 1 or a b with a large denominator) falls back to the
 * 1/80640 grid and is counted in `report`.
 */
function step(b: Beat, div: number, k: number, report?: RoundingReport): Beat {
  requireDiv(div);
  requireInt(k, 'k');
  if (k === 0) return b;
  const wd = b.w * b.d;
  const whole = wd + b.n;
  const left = whole * div;
  const right = k * b.d;
  const num = left + right;
  const den = b.d * div;
  if (
    Number.isSafeInteger(wd) &&
    Number.isSafeInteger(whole) &&
    Number.isSafeInteger(left) &&
    Number.isSafeInteger(right) &&
    Number.isSafeInteger(num) &&
    Number.isSafeInteger(den)
  ) {
    return normNum(num, den, report);
  }
  const big = bigOf(b);
  const DIV = BigInt(div);
  return normBig(big.num * DIV + BigInt(k) * big.den, big.den * DIV, report);
}

// ---------------------------------------------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------------------------------------------

function isNormal(b: Beat): boolean {
  const { w, n, d } = b;
  return (
    Number.isInteger(w) &&
    w >= I32_MIN &&
    w <= I32_MAX &&
    Number.isInteger(d) &&
    d >= 1 &&
    d <= U32_MAX &&
    Number.isInteger(n) &&
    n >= 0 &&
    n < d &&
    (n === 0 ? d === 1 : gcd(n, d) === 1)
  );
}

function key(b: Beat): string {
  return b.n === 0 ? String(b.w) : `${b.w}+${b.n}/${b.d}`;
}

export const Beats: BeatApi = {
  ZERO,
  ONE,
  make,
  fromInt,
  fromTriple,
  tryFromTriple,
  toTriple: (b) => [b.w, b.n, b.d],
  // Phira `Triple::beats`: a as f64 + b as f64 / c as f64, in exactly this order.
  toF64: (b) => b.w + b.n / b.d,
  tripleToF64: (t) => t[0] + t[1] / t[2],
  fromDecimalString,
  fromF64,
  cmp,
  eq: (a, b) => a.w === b.w && a.n === b.n && a.d === b.d,
  lt: (a, b) => cmp(a, b) < 0,
  lte: (a, b) => cmp(a, b) <= 0,
  min: (a, b) => (cmp(b, a) < 0 ? b : a),
  max: (a, b) => (cmp(b, a) > 0 ? b : a),
  add,
  sub,
  mulInt,
  mulRat,
  divInt,
  snap: (b, div) => quantize(b, div, 'round'),
  floorTo: (b, div) => quantize(b, div, 'floor'),
  ceilTo: (b, div) => quantize(b, div, 'ceil'),
  step: (b, div, k) => step(b, div, k),
  isNormal,
  key,
};

// ---------------------------------------------------------------------------------------------------------------
// Additive helpers (not part of BeatApi)
// ---------------------------------------------------------------------------------------------------------------

/**
 * `Beats.snap` / `floorTo` / `ceilTo` with a `RoundingReport`: `report.rounded` is incremented when the exact
 * multiple of 1/div is not representable (div > 2^32 - 1) and the result fell back to the 1/80640 grid.
 */
export function quantizeBeat(b: Beat, div: number, mode: QuantizeMode, report?: RoundingReport): Beat {
  return quantize(b, div, mode, report);
}

/** `Beats.step` with a `RoundingReport` counting results that fell back to the 1/80640 grid. */
export function stepBeat(b: Beat, div: number, k: number, report?: RoundingReport): Beat {
  return step(b, div, k, report);
}

/** -b, exact. */
export function negBeat(b: Beat): Beat {
  return b.n === 0 ? fin(-b.w, 0, 1) : fin(-b.w - 1, b.d - b.n, b.d);
}

/** |b|, exact. */
export function absBeat(b: Beat): Beat {
  return b.w < 0 ? negBeat(b) : b;
}

/** Sign of b. */
export function beatSign(b: Beat): -1 | 0 | 1 {
  return b.w < 0 ? -1 : b.w > 0 || b.n > 0 ? 1 : 0;
}

/** Exact value as a BigInt rational. */
export function beatToRat(b: Beat): BigRational {
  return Rat.of(BigInt(b.w) * BigInt(b.d) + BigInt(b.n), BigInt(b.d));
}

/** Rational to Beat with the computed-result overflow policy (rounds to 1/80640 when the denominator overflows). */
export function beatFromRat(r: BigRational, report?: RoundingReport): Beat {
  return normBig(r.num, r.den, report);
}

/** Rational to Beat without rounding: null when the denominator exceeds 2^32 - 1 or w leaves i32. */
export function beatFromRatExact(r: BigRational): Beat | null {
  return normBigExact(r.num, r.den);
}

/**
 * Nearest multiple of 1/div to the EXACT value of the double x (ties toward +infinity), e.g. a seconds-derived
 * f64 beat snapped to the grid. Throws RangeError for non-finite x, a div that is not a positive safe integer, or
 * an out-of-range result. For div > 2^32 - 1 a non-representable multiple falls back to the 1/80640 grid and is
 * counted in `report`.
 */
export function beatFromF64Snapped(x: number, div: number, report?: RoundingReport): Beat {
  requireDiv(div);
  if (!Number.isFinite(x)) throw new RangeError(`beat: not a finite number (${String(x)})`);
  const exact = dyadicOf(x);
  const DIV = BigInt(div);
  return normBig(roundHalfUpDivBig(exact.num * DIV, exact.den), DIV, report);
}

/** Inverse of `Beats.key` ("12", "12+3/4", "-3+1/4"); null when the string is not a canonical key. */
export function parseBeatKey(s: string): Beat | null {
  const m = /^(-?(?:0|[1-9]\d*))(?:\+([1-9]\d*)\/([1-9]\d*))?$/.exec(s);
  if (m === null) return null;
  const w = Number(m[1]);
  if (m[2] === undefined || m[3] === undefined) {
    return Number.isInteger(w) && w >= I32_MIN && w <= I32_MAX ? fromInt(w) : null;
  }
  const b: Beat = { w, n: Number(m[2]), d: Number(m[3]) };
  return isNormal(b) ? b : null;
}
